/**
 * InlinePromptBox Component
 *
 * Inline prompt editor ported directly from orbit-summer-2022-demo.
 *
 * Source files from demo:
 * - src/components/prompt/PromptComponents.tsx (Icon, DueBadge, PromptText)
 * - src/components/prompt/PromptBox.tsx (main container logic)
 * - src/components/prompt/CollapsedPromptIcon.tsx (collapsed state icon)
 * - src/components/prompt/PromptEllipses.tsx (three dots menu)
 * - src/components/ContextualMenu.tsx (delete prompt menu)
 *
 * Features:
 * - Starburst icon with state-based images (null/editing/active)
 * - Red "due" badge dot in top-right corner of starburst
 * - FloatingCollapsed context: shows only starburst when not hovered/editing
 * - Expands on hover to show full prompt form
 * - Two contentEditable fields: "Type a prompt here." / "Type a response here."
 * - Escape key blurs and collapses
 * - Enter creates new line (contentEditable default behavior)
 * - Collapses on blur/click outside
 * - Three dots ellipsis menu with "Delete Prompt (D)" option
 */

import React, {
  forwardRef,
  useEffect,
  useRef,
  useState,
  ForwardedRef,
  KeyboardEvent,
} from "react";

// Animation timing from demo PromptComponents.tsx line 10
const ANIMATION_TIME_MSEC = 48.0;

// Container width from demo PromptBox.tsx line 205 (left: -284px offset)
const PROMPT_BOX_WIDTH = 284;

export interface InlinePrompt {
  id: string;
  front: string;
  back: string;
  isSaved: boolean;
  isNew?: boolean;
  isDue?: boolean; // For the red dot badge
  /** Computed viewport-relative top position (changes with zoom) */
  top: number;
  /** Page index this prompt is anchored to (0-indexed) */
  pageIndex?: number;
  /** Normalized position within page (0-1) for zoom-invariant positioning */
  pageRelativeTop?: number;
  /** Orbit Task ID if this prompt has been persisted to the database */
  orbitTaskId?: string;
}

export interface InlinePromptBoxProps {
  prompt: InlinePrompt;
  onSave: (id: string, front: string, back: string) => void;
  onDelete?: (id: string) => void;
  marginX: number;
}

// ============================================================================
// Icon Component - ported from demo PromptComponents.tsx lines 80-154
// ============================================================================

type IconState = "null" | "editing" | "active" | "plus";

function getIconUrl(state: IconState): string {
  switch (state) {
    case "editing":
      return "/images/Starburst-Edit.png";
    case "active":
      return "/images/Starburst-Active.png";
    case "plus":
      return "/images/Plus.png";
    case "null":
    default:
      return "/images/Starburst-Null.png";
  }
}

interface IconProps {
  isHovered: boolean;
  isSaved: boolean;
  isEditing: boolean;
  isDue: boolean;
  isAnchorHovered?: boolean;
}

/**
 * Icon component - ported from demo PromptComponents.tsx lines 133-154
 */
function Icon({ isHovered, isSaved, isEditing, isDue, isAnchorHovered = false }: IconProps) {
  let iconState: IconState;
  let isStarburst: boolean;

  // Logic from demo PromptComponents.tsx lines 83-97
  if (isEditing || isAnchorHovered) {
    isStarburst = true;
    iconState = "editing";
  } else if (isSaved) {
    isStarburst = true;
    iconState = "active";
  } else if (isHovered) {
    isStarburst = false;
    iconState = "plus";
  } else {
    isStarburst = true;
    iconState = "null";
  }

  return (
    // IconBase from demo lines 80-112
    <div
      style={{
        width: 24,
        height: 24,
        backgroundImage: `url(${getIconUrl(iconState)})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        // Optical centering corrections from demo lines 104-107
        marginTop: isStarburst ? -0.5 : -2,
        marginLeft: isStarburst ? 0.5 : 0,
        marginRight: isStarburst ? -0.5 : 0,
        marginBottom: -4, // HACK from demo line 107
        flex: "0 0 auto",
        position: "relative",
      }}
    >
      {/* DueBadge from demo lines 114-131 */}
      {isDue && (
        // DueBadgeOuter - demo lines 121-131
        <div
          style={{
            position: "relative",
            left: 16,
            top: -2,
            borderRadius: "50%",
            width: 10,
            height: 10,
            paddingLeft: 1.5,
            paddingTop: 1.5,
            backgroundColor: "var(--bgPrimary)",
          }}
        >
          {/* DueBadgeInner - demo lines 114-119 */}
          <div
            style={{
              height: 7,
              width: 7,
              borderRadius: "50%",
              backgroundColor: "var(--accentPrimary)",
            }}
          />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PromptEllipses - ported from demo PromptEllipses.tsx
// ============================================================================

interface PromptEllipsesProps {
  onClick: () => void;
}

/**
 * PromptEllipses - Three dots menu button
 * Ported from demo src/components/prompt/PromptEllipses.tsx
 */
function PromptEllipses({ onClick }: PromptEllipsesProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    // Container - demo lines 3-12
    <div
      style={{
        width: 32,
        height: 29,
        background: "linear-gradient(-90deg, #f9f6f1 55.32%, #f9f6f1 69.38%, rgba(249, 246, 241, 0) 101.06%)",
      }}
    >
      {/* HoverContainer - demo lines 14-47 */}
      <div
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          width: 15,
          height: 23,
          borderRadius: 4,
          position: "relative",
          top: 3,
          left: 8.5,
          cursor: "pointer",
          backgroundColor: isHovered ? "var(--hoverLayer)" : "transparent",
        }}
      >
        {/* Three dots - demo lines 53-61 */}
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: 3,
              height: 3,
              backgroundColor: isHovered ? "var(--accentPrimary)" : "var(--fgSecondarySmall)",
              position: "relative",
              left: 6,
              top: 5 + i * 2,
              borderRadius: "50%",
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// ContextualMenu - ported from demo ContextualMenu.tsx
// ============================================================================

interface ContextualMenuItemProps {
  title: string;
  onClick: () => void;
  shortcutKey: string;
  isEnabled: boolean;
}

/**
 * MenuItem with keyboard shortcut
 * Ported from demo src/components/MenuItem.tsx
 */
function MenuItem({ title, onClick, shortcutKey, isEnabled }: ContextualMenuItemProps) {
  const [isHovered, setIsHovered] = useState(false);

  // Handle keyboard shortcut - demo MenuItem.tsx lines 34-44
  useEffect(() => {
    const onShortcutKey = (e: globalThis.KeyboardEvent) => {
      if (e.key.toLowerCase() === shortcutKey.toLowerCase() && isEnabled) {
        e.preventDefault();
        onClick();
      }
    };

    document.addEventListener("keydown", onShortcutKey);
    return () => document.removeEventListener("keydown", onShortcutKey);
  }, [shortcutKey, isEnabled, onClick]);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: 12,
        height: 36,
        margin: 0,
        backgroundColor: "var(--bgPrimary)",
        border: "none",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        justifyContent: "space-between",
        cursor: "pointer",
        position: "relative",
      }}
    >
      {/* Hover overlay */}
      {isHovered && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "var(--hoverLayer)",
            pointerEvents: "none",
          }}
        />
      )}
      <span
        style={{
          fontFamily: "Dr-Bold, system-ui, sans-serif",
          fontSize: 14,
          lineHeight: "17px",
          letterSpacing: "0.04em",
          color: "var(--accentPrimary)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {title}
      </span>
      {/* ShortcutKey box */}
      <div
        style={{
          position: "relative",
          width: 20,
          height: 20,
          background: "var(--bgSecondary)",
          borderRadius: 2,
          border: isHovered ? "2px solid var(--fgTertiary)" : "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1,
        }}
      >
        <span
          style={{
            fontFamily: "Dr-ExtraBold, system-ui, sans-serif",
            fontSize: 15,
            lineHeight: "17px",
            textAlign: "center",
            letterSpacing: "0.03em",
            color: "var(--fgSecondarySmall)",
          }}
        >
          {shortcutKey}
        </span>
      </div>
    </button>
  );
}

interface ContextualMenuProps {
  items: ContextualMenuItemProps[];
  onMouseLeave: () => void;
}

/**
 * ContextualMenu - Delete prompt menu
 * Ported from demo src/components/ContextualMenu.tsx
 */
function ContextualMenu({ items, onMouseLeave }: ContextualMenuProps) {
  return (
    // ContextMenuContainer - demo lines 17-26
    <div
      onMouseLeave={onMouseLeave}
      style={{
        width: 254,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: 4,
        background: "var(--bgPrimary)",
        border: "3px solid var(--fgTertiary)",
        boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.07)",
      }}
    >
      {items.map((item, i) => (
        <MenuItem key={i} {...item} />
      ))}
    </div>
  );
}

// ============================================================================
// CollapsedPromptIcon - ported from demo CollapsedPromptIcon.tsx
// ============================================================================

interface CollapsedPromptIconProps {
  isSaved: boolean;
  isHovered: boolean;
  isEditing: boolean;
  isDue: boolean;
  isAnchorHovered?: boolean;
}

/**
 * CollapsedPromptIcon - ported from demo CollapsedPromptIcon.tsx lines 46-81
 * Shows just the starburst icon with a circular background when collapsed
 */
function CollapsedPromptIcon({
  isSaved,
  isHovered,
  isEditing,
  isDue,
  isAnchorHovered = false,
}: CollapsedPromptIconProps) {
  // Background color logic from demo lines 24-32
  const getBackgroundColor = () => {
    if (isAnchorHovered) {
      return "var(--selectionHover)";
    } else if (isSaved && !isHovered && !isEditing) {
      return "var(--bgSecondary)";
    }
    return "transparent";
  };

  return (
    // CollapsedIconContainer - demo lines 11-19
    <div
      style={{
        pointerEvents: "all",
        minWidth: 32,
        height: 32,
        position: "relative",
        top: -6,
        cursor: "pointer",
      }}
    >
      {/* CollapsedIconBackground - demo lines 21-36 */}
      <div
        style={{
          backgroundColor: getBackgroundColor(),
          borderRadius: "50%",
          width: "100%",
          height: "100%",
        }}
      />
      {/* Icon positioned inside - demo lines 64-78 */}
      <div
        style={{
          position: "relative",
          top: -26,
          left: 4,
        }}
      >
        <Icon
          isHovered={isHovered}
          isAnchorHovered={isAnchorHovered}
          isSaved={isSaved}
          isEditing={isEditing}
          isDue={isDue}
        />
      </div>
    </div>
  );
}

// ============================================================================
// PromptText - ported from demo PromptComponents.tsx lines 156-253
// ============================================================================

interface PromptTextFieldProps {
  side: "front" | "back";
  isHovered: boolean;
  isSaved: boolean;
  isEditing: boolean;
  placeholder: string;
  value: string;
  onFocus: () => void;
  onBlur: (value: string) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
  autoFocus?: boolean;
}

const PromptTextField = forwardRef(function PromptTextField(
  {
    side,
    isHovered,
    isSaved,
    isEditing,
    placeholder,
    value,
    onFocus,
    onBlur,
    onKeyDown,
    autoFocus,
  }: PromptTextFieldProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const localRef = useRef<HTMLDivElement | null>(null);

  // Set initial content - demo lines 168-180
  useEffect(() => {
    if (localRef.current && value && localRef.current.innerText !== value) {
      localRef.current.innerHTML = value;
    }
  }, [value]);

  // Auto-focus handling - demo lines 296-301
  useEffect(() => {
    if (autoFocus && localRef.current) {
      localRef.current.focus({ preventScroll: true });
    }
  }, [autoFocus]);

  const handleBlur = () => {
    const text = localRef.current?.innerText || "";
    onBlur(text);
  };

  // Opacity logic from demo lines 206-241
  const getOpacity = () => {
    if (side === "back") {
      if (isSaved) return 1.0;
      if (isHovered) return 0.7;
      return 0.0;
    }
    return isHovered || isSaved ? 1.0 : 0.696;
  };

  return (
    // PromptText div from demo lines 182-252
    <div
      ref={(r) => {
        localRef.current = r;
        if (typeof ref === "function") {
          ref(r);
        } else if (ref) {
          ref.current = r;
        }
      }}
      contentEditable={isSaved}
      suppressContentEditableWarning
      onFocus={onFocus}
      onBlur={handleBlur}
      onKeyDown={onKeyDown}
      data-placeholder={placeholder}
      spellCheck={isEditing}
      style={{
        // Styles from demo lines 199-227
        fontFamily: "Dr-Medium, system-ui, sans-serif",
        fontSize: 14,
        lineHeight: "17px",
        letterSpacing: "0.04em",
        color: side === "back" && isSaved ? "var(--fgSecondarySmall)" : "var(--fgPrimary)",
        opacity: getOpacity(),
        outline: "none",
        hyphens: "auto",
        cursor: isSaved ? "text" : "default",
        wordBreak: "break-word",
        transition: `${ANIMATION_TIME_MSEC / 1000}s ease-out`,
        minHeight: 17,
        caretColor: "var(--accentPrimary)",
      }}
    />
  );
});

// ============================================================================
// InlinePromptBox - Main component ported from demo PromptBox.tsx
// ============================================================================

export default function InlinePromptBox({
  prompt,
  onSave,
  onDelete,
  marginX,
}: InlinePromptBoxProps) {
  // State from demo PromptBox.tsx lines 237-246
  const [_isHovered, setIsHovered] = useState(false);
  const isHovered = _isHovered;
  const [isEditing, setIsEditing] = useState(prompt.isNew ?? false);
  const [showPromptBack, setShowPromptBack] = useState(prompt.isSaved);
  const [front, setFront] = useState(prompt.front);
  const [back, setBack] = useState(prompt.back);

  // Context menu state - demo lines 242-244
  const [contextMenuOpen, setContextMenuOpen] = useState(false);

  const hideBackTimeout = useRef<number | undefined>();
  const frontRef = useRef<HTMLDivElement | null>(null);
  const backRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const isSaved = prompt.isSaved;
  const isDue = prompt.isDue ?? true; // Default to showing the badge

  // Determine if collapsed (show only icon) or expanded (show full form)
  // In FloatingCollapsed context: collapsed when not hovered AND not editing
  const isCollapsed = !isHovered && !isEditing && isSaved && !contextMenuOpen;

  // Show/hide back field with animation timing - demo lines 252-263
  useEffect(() => {
    if (!isHovered && !isSaved) {
      hideBackTimeout.current = window.setTimeout(() => {
        setShowPromptBack(false);
      }, ANIMATION_TIME_MSEC);
    } else if (isHovered || isSaved) {
      clearTimeout(hideBackTimeout.current);
      hideBackTimeout.current = undefined;
      setShowPromptBack(true);
    }
  }, [isHovered, isSaved]);

  // startEditing - demo lines 265-280
  const startEditing = (editingFront: boolean) => {
    setIsEditing(true);
    const el = editingFront ? frontRef.current : backRef.current;
    const sel = window.getSelection();
    const range = document.createRange();
    if (el && sel && range) {
      el.innerHTML = editingFront ? front : back;
      range.selectNodeContents(el);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  // endEditing - demo lines 282-293
  const endEditing = (editingFront: boolean, value: string) => {
    if (editingFront) {
      setFront(value);
    } else {
      setBack(value);
    }
    setIsEditing(false);
    setContextMenuOpen(false);
    // Save prompt
    const finalFront = editingFront ? value : front;
    const finalBack = editingFront ? back : value;
    onSave(prompt.id, finalFront, finalBack);
  };

  // Handle Escape key to blur and collapse
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      // Blur the active element to trigger endEditing
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
    // Enter creates new line by default in contentEditable - no action needed
  };

  // Click outside to collapse - detect clicks outside container
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        isEditing
      ) {
        // Blur active element if it's inside our container
        if (
          document.activeElement instanceof HTMLElement &&
          containerRef.current.contains(document.activeElement)
        ) {
          document.activeElement.blur();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEditing]);

  // Context menu items - demo PromptBox.tsx lines 308-321
  const contextMenuItems: ContextualMenuItemProps[] = [
    {
      title: "Delete Prompt",
      onClick: () => {
        setContextMenuOpen(false);
        setIsHovered(false);
        if (onDelete) onDelete(prompt.id);
      },
      shortcutKey: "D",
      isEnabled: contextMenuOpen,
    },
  ];

  // Border color logic from demo PromptBox.tsx lines 103-132
  const getBorderColor = () => {
    if (isCollapsed) {
      return "transparent";
    }
    if ((isHovered && !isSaved) || isEditing) {
      return "var(--accentPrimary)";
    }
    if (isSaved && !isEditing) {
      return "var(--accentSecondary)";
    }
    return "transparent";
  };

  // Background color logic from demo lines 143-167
  const getBackground = () => {
    if (isCollapsed) {
      return "transparent";
    }
    if (isSaved && (isHovered || isEditing)) {
      return "var(--bgPrimary)";
    }
    if (isHovered && !isSaved) {
      return "var(--bgContent)";
    }
    return "transparent";
  };

  // Box shadow logic from demo lines 133-142
  const getBoxShadow = () => {
    if (isCollapsed) return "none";
    if ((isHovered || isEditing) && (!isSaved || true)) {
      return "0px 1px 3px rgba(0, 0, 0, 0.07), 0px 5px 10px rgba(0, 0, 0, 0.08)";
    }
    return "none";
  };

  return (
    <div
      ref={containerRef}
      className="inlinePromptBox"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        if (!isEditing) setContextMenuOpen(false);
      }}
      onClick={() => {
        if (!isSaved) {
          onSave(prompt.id, front, back);
        }
      }}
      style={{
        position: "absolute",
        left: marginX,
        top: prompt.top,
        // Container styles from demo PromptBox.tsx lines 76-213
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        padding: isCollapsed ? "0" : "8px 8px 10px 9px",
        gap: 8,
        cursor: isSaved && !isEditing ? "auto" : "pointer",
        borderLeft: isCollapsed ? "none" : `3px solid ${getBorderColor()}`,
        background: getBackground(),
        boxShadow: getBoxShadow(),
        transition: `${ANIMATION_TIME_MSEC / 1000}s ease-out`,
        // Width from demo - 284px when expanded
        width: isCollapsed ? "auto" : PROMPT_BOX_WIDTH,
        // Higher z-index when expanded/hovered to appear above translation overlay (z-index: 90)
        zIndex: isCollapsed ? 100 : 200,
        // pointer-events logic from demo line 211
        pointerEvents: isCollapsed && !isHovered ? "auto" : "auto",
      }}
    >
      {/* Collapsed state - show only CollapsedPromptIcon */}
      {isCollapsed ? (
        <CollapsedPromptIcon
          isSaved={isSaved}
          isHovered={isHovered}
          isEditing={isEditing}
          isDue={isDue}
        />
      ) : (
        <>
          {/* Expanded state - show icon and text fields */}
          {/* Icon - demo createInlineIcon() lines 334-348 */}
          <Icon
            isHovered={isHovered}
            isSaved={isSaved}
            isEditing={isEditing}
            isDue={isDue}
          />

          {/* Prompt text fields - demo createPromptText() lines 350-393 */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              padding: 0,
              gap: 8,
              flex: 1,
            }}
          >
            {/* Front field (question) */}
            <PromptTextField
              ref={frontRef}
              side="front"
              isHovered={isHovered}
              isSaved={isSaved}
              isEditing={isEditing}
              placeholder="Type a prompt here."
              value={front}
              onFocus={() => startEditing(true)}
              onBlur={(value) => endEditing(true, value)}
              onKeyDown={handleKeyDown}
              autoFocus={prompt.isNew}
            />

            {/* Back field (answer) - shown on hover or when saved */}
            {(showPromptBack || isSaved) && (
              <PromptTextField
                ref={backRef}
                side="back"
                isHovered={isHovered}
                isSaved={isSaved}
                isEditing={isEditing}
                placeholder="Type a response here."
                value={back}
                onFocus={() => startEditing(false)}
                onBlur={(value) => endEditing(false, value)}
                onKeyDown={handleKeyDown}
              />
            )}
          </div>

          {/* Ellipses menu - demo PromptBox.tsx lines 486-508 */}
          {isSaved && (isHovered || isEditing) && (
            <div
              style={{
                position: "absolute",
                top: 3,
                right: 1,
              }}
            >
              <PromptEllipses onClick={() => setContextMenuOpen(true)} />
            </div>
          )}

          {/* Context menu - demo PromptBox.tsx lines 509-532 */}
          {contextMenuOpen && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: "100%",
                display: "flex",
                flexDirection: "row",
                zIndex: 1000,
              }}
            >
              <div style={{ width: 6 }} />
              <ContextualMenu
                items={contextMenuItems}
                onMouseLeave={() => setContextMenuOpen(false)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
